import { Marker } from 'maplibre-gl';
import { C } from '../map/tokens.js';
import { toast } from './toast.js';

function markerElement() {
  const el = document.createElement('div');
  el.style.cssText = `width:14px;height:14px;border-radius:50%;background:${C.favorite};
    border:2.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);cursor:pointer`;
  return el;
}

/** Gespeicherte Reise statt Ort? Dann fuehrt der Eintrag in die Routenplanung. */
const istReise = (f) => Boolean(f.reise);

const MITTEL = { auto: 'Auto', bicycle: 'Velo', pedestrian: 'Zu Fuss', transit: 'ÖV' };

/** „ÖV · ab Do 09.10., 08:30“ - oder nur das Verkehrsmittel, wenn keine Zeit dabei ist. */
function reiseMeta(reise) {
  const mittel = MITTEL[reise.mode] || 'Route';
  if (!reise.zeit) return mittel;
  const d = new Date(reise.zeit);
  if (Number.isNaN(d.getTime())) return mittel;
  const wann = d.toLocaleString('de-CH', {
    weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
  });
  return `${mittel} · ${reise.ankunft ? 'an' : 'ab'} ${wann}`;
}

export function createFavoritesPanel({ map, host, badge, modeEl, backend, getProfile, onNavigate, onReise }) {
  let items = [];
  const markers = new Map();

  function syncMarkers() {
    for (const [id, m] of markers) {
      if (!items.some((f) => f.id === id && !istReise(f))) { m.remove(); markers.delete(id); }
    }
    for (const f of items) {
      // Eine Reise ist kein Ort - sie bekommt keine Marke in der Karte.
      if (istReise(f) || markers.has(f.id)) continue;
      const m = new Marker({ element: markerElement(), anchor: 'center' })
        .setLngLat([f.lng, f.lat])
        .addTo(map);
      m.getElement().title = f.name;
      m.getElement().addEventListener('click', () => flyTo(f));
      markers.set(f.id, m);
    }
  }

  function flyTo(f) {
    map.flyTo({ center: [f.lng, f.lat], zoom: f.zoom || 16, duration: 700 });
    onNavigate?.();
  }

  function render() {
    host.textContent = '';
    badge.textContent = String(items.length);
    badge.hidden = items.length === 0;

    const profile = getProfile();
    if (modeEl) {
      modeEl.textContent = profile
        ? profile.local ? `lokal: ${profile.name}` : profile.name
        : 'nicht angemeldet';
    }

    if (!items.length) {
      const p = document.createElement('div');
      p.className = 'empty';
      p.textContent = 'Noch keine Favoriten gespeichert.';
      host.append(p);
      syncMarkers();
      return;
    }

    for (const f of items) {
      const row = document.createElement('div');
      row.className = 'fav';

      const main = document.createElement('button');
      main.type = 'button';
      main.className = 'fav__main';
      const name = document.createElement('span');
      name.className = 'fav__name';
      name.textContent = f.name;
      const meta = document.createElement('span');
      meta.className = 'fav__meta';
      meta.textContent = istReise(f)
        ? reiseMeta(f.reise)
        : [f.kind, `${f.lat.toFixed(4)}, ${f.lng.toFixed(4)}`].filter(Boolean).join(' · ');
      main.append(name, meta);
      if (istReise(f)) {
        main.classList.add('fav__main--reise');
        main.addEventListener('click', () => { onReise?.(f.reise); onNavigate?.(); });
      } else {
        main.addEventListener('click', () => flyTo(f));
      }

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'fav__del';
      del.title = istReise(f) ? 'Reise löschen' : 'Favorit löschen';
      del.setAttribute('aria-label', `${istReise(f) ? 'Reise' : 'Favorit'} ${f.name} löschen`);
      del.textContent = '×';
      del.addEventListener('click', () => remove(f));

      row.append(main, del);
      host.append(row);
    }
    syncMarkers();
  }

  async function load() {
    try {
      items = (await backend.favorites.list(getProfile())) || [];
    } catch {
      items = [];
      toast('Favoriten konnten nicht geladen werden.');
    }
    render();
  }

  async function add(fav) {
    try {
      const entry = await backend.favorites.add(getProfile(), fav);
      items = [entry, ...items];
      render();
      toast(istReise(entry) ? `Reise „${fav.name}“ gespeichert.` : `„${fav.name}“ gespeichert.`);
    } catch (err) {
      toast(err.message || 'Speichern fehlgeschlagen.');
    }
  }

  async function remove(fav) {
    try {
      await backend.favorites.remove(getProfile(), fav.id);
      items = items.filter((f) => f.id !== fav.id);
      render();
    } catch {
      toast('Löschen fehlgeschlagen.');
    }
  }

  return { load, add, remove, render, get items() { return items; } };
}
